import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  DatePicker,
  Banner,
  HelperText,
  Modal,
  Select,
  Switch,
  TextArea,
} from '@scripsteam/scrips-signal-ds';
import { useCohorts } from '@/cohorts/services/use-cohorts';
import {
  MAX_BULK_PATIENTS,
  isRetryableOutcome,
  mergeBulkResults,
  useBulkEnrol,
  useEnrolInCohort,
} from '@/cohorts/services/use-cohort-mutations';
import { isOverlapConflict, serverMessage } from '@/cohorts/services/cohort-errors';
import { toBusinessDate } from '@/cohorts/services/business-date';
import type { BulkEnrolResponse } from '@/types/cohort-api';
import { BulkEnrolResults } from './bulk-enrol-results';

/**
 * Enrol patients in a cohort (PROD-3206, Samer 13 Oct: "from the practitioner app for nurses,
 * with single and bulk selection"). One patient → the single memberships call (409 = overlap).
 * More than one → the bulk call, then a per-patient result list. "Retry N refused" reopens the
 * form for the refused (Invalid) patients only, so the dates or notes can change first.
 * Mounted only behind `isCohortEnrolmentOn()` (deploy flag + `?x=cohorts`). No role gate: the API enforces auth.
 */

export interface EnrolPatient {
  id: string;
  name: string;
  mrn: string | null;
}

export interface EnrolInCohortDialogProps {
  open: boolean;
  onClose: () => void;
  patients: EnrolPatient[];
  /** Called after the server answered: a single enrol succeeded, or a bulk result came back. */
  onEnrolled?: () => void;
}

const OVERLAP_MESSAGE = 'This patient already has an overlapping membership in this cohort.';

export function EnrolInCohortDialog(props: EnrolInCohortDialogProps) {
  if (!props.open) return null;
  return <EnrolInCohortForm {...props} />;
}

function EnrolInCohortForm({ onClose, patients, onEnrolled }: EnrolInCohortDialogProps) {
  const cohorts = useCohorts();
  const enrol = useEnrolInCohort();
  const bulk = useBulkEnrol();

  const [cohortId, setCohortId] = useState<string>('');
  const [startsOn, setStartsOn] = useState<Date | undefined>(() => new Date());
  const [endsOn, setEndsOn] = useState<Date | undefined>(undefined);
  const [activate, setActivate] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkEnrolResponse | null>(null);
  // Set while the form is reopened to retry the refused rows of `result`.
  const [retryIds, setRetryIds] = useState<string[] | null>(null);
  const showResults = result !== null && retryIds === null;

  const targets = retryIds ? patients.filter((p) => retryIds.includes(p.id)) : patients;
  const single = patients.length === 1;
  const overCap = targets.length > MAX_BULK_PATIENTS;

  const busy = enrol.isPending || bulk.isPending;
  const cohortName = cohorts.data?.find((c) => c.id === cohortId)?.name ?? 'the cohort';

  function handleSubmit() {
    if (!cohortId) return setError('Pick a cohort.');
    if (!startsOn) return setError('Enter a start date.');
    if (endsOn && toBusinessDate(endsOn) < toBusinessDate(startsOn)) {
      return setError('The end date must be on or after the start date.');
    }
    if (overCap) return setError(`You can enrol up to ${MAX_BULK_PATIENTS} patients at a time.`);
    setError(null);
    const body = {
      cohortId,
      startsOn: toBusinessDate(startsOn),
      endsOn: endsOn ? toBusinessDate(endsOn) : null,
      activate,
      notes: notes.trim() || null,
    };
    if (single) {
      enrol.mutate(
        { ...body, patientId: patients[0].id },
        {
          onSuccess: () => {
            toast.success(`${patients[0].name} enrolled in ${cohortName}`);
            onEnrolled?.();
            onClose();
          },
          onError: (e) => setError(isOverlapConflict(e) ? OVERLAP_MESSAGE : serverMessage(e)),
        },
      );
      return;
    }
    const request = retryIds
      ? bulk.retry(retryIds, body)
      : bulk.submit({ ...body, patientIds: patients.map((p) => p.id) });
    request
      .then((next) => {
        setResult((prev) => (prev && retryIds ? mergeBulkResults(prev, next) : next));
        setRetryIds(null);
        onEnrolled?.();
      })
      .catch((e: unknown) => setError(serverMessage(e)));
  }

  function handleRetry() {
    if (!result) return;
    setError(null);
    setRetryIds(result.results.filter(isRetryableOutcome).map((r) => r.patientId));
  }

  const title = retryIds
    ? `Retry ${retryIds.length} refused ${retryIds.length === 1 ? 'patient' : 'patients'}`
    : single
      ? `Enrol ${patients[0].name} in a cohort`
      : `Enrol ${patients.length} patients in a cohort`;

  const submitLabel = retryIds
    ? `Retry ${retryIds.length}`
    : single
      ? 'Enrol'
      : `Enrol ${patients.length}`;

  const footer = showResults ? (
    <Button type="button" onClick={onClose}>
      Done
    </Button>
  ) : (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={retryIds ? () => setRetryIds(null) : onClose}
        disabled={busy}
      >
        {retryIds ? 'Back to results' : 'Cancel'}
      </Button>
      <Button type="button" onClick={handleSubmit} disabled={busy || overCap}>
        {busy ? 'Enrolling…' : submitLabel}
      </Button>
    </>
  );

  return (
    <Modal open onClose={onClose} title={title} size={showResults ? 'lg' : 'md'} footer={footer}>
      {showResults && result ? (
        <BulkEnrolResults
          result={result}
          patients={patients}
          onRetry={handleRetry}
        />
      ) : (
        <div data-slot="enrol-form" className="flex flex-col gap-4">
          <Select
            label="Cohort"
            required
            placeholder={cohorts.isPending ? 'Loading cohorts…' : 'Pick a cohort'}
            options={(cohorts.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={cohortId || undefined}
            onChange={(v) => setCohortId(v)}
            disabled={busy || cohorts.isPending}
            helperText={
              cohorts.isSuccess && cohorts.data.length === 0 ? 'No cohorts set up yet.' : undefined
            }
            error={cohorts.isError ? 'Could not load cohorts.' : undefined}
          />
          <div data-slot="enrol-two" className="grid grid-cols-2 gap-3">
            <DatePicker
              label="Starts on"
              required
              value={startsOn}
              onChange={setStartsOn}
              disabled={busy}
            />
            <DatePicker
              label="Ends on"
              value={endsOn}
              onChange={setEndsOn}
              minDate={startsOn}
              placeholder="Optional"
              disabled={busy}
            />
          </div>
          <label data-slot="enrol-switch" className="flex items-center justify-between gap-3 text-size-sm text-text-primary">
            <span>
              Activate now
              <span data-slot="enrol-switch-hint" className="block text-text-muted">Off saves the enrolment as a draft.</span>
            </span>
            <Switch
              aria-label="Activate now"
              checked={activate}
              onCheckedChange={setActivate}
              disabled={busy}
            />
          </label>
          <TextArea
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            disabled={busy}
          />
          {overCap ? (
            <HelperText tone="error">
              {`You selected ${targets.length} patients. You can enrol up to ${MAX_BULK_PATIENTS} at a time.`}
            </HelperText>
          ) : null}
        </div>
      )}
      {error ? (
        <div data-slot="enrol-error">
          <Banner variant="error" role="alert">
            {error}
          </Banner>
        </div>
      ) : null}
    </Modal>
  );
}
